import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Tiny AsyncStorage wrapper for stale-while-revalidate caching.
 *
 * Use case: hydrate a screen's data instantly on cold start from cache, then
 * refetch in the background. Cache values are JSON-serialised and keyed
 * per-user (caller is responsible for namespacing with user_id).
 *
 * Failures are swallowed — the network fetch is always the source of truth,
 * the cache is purely an optimisation.
 */

const PREFIX = 'cache:v1:';

export async function getCached<T>(key: string): Promise<T | null> {
  try {
    const raw = await AsyncStorage.getItem(PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export async function setCached<T>(key: string, value: T): Promise<void> {
  try {
    await AsyncStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export async function clearCached(key: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(PREFIX + key);
  } catch {
    // ignore
  }
}

/**
 * Clear all cache entries (e.g. on signOut).
 */
export async function clearAllCached(): Promise<void> {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const toRemove = keys.filter((k) => k.startsWith(PREFIX));
    if (toRemove.length > 0) await AsyncStorage.multiRemove(toRemove);
  } catch {
    // ignore
  }
}
